import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Tabs from '../../Components/common/Tabs/Tabs';
import JobsFilter from '../../Components/common/Filters/JobsFilter';
import { JobsFeed, AppliedJobs, SavedJobs } from '../../Components/pages/jobs';
import QuickApply from '../../Components/quickapply';
import classes from './jobs.module.scss';
import api from '../../api/apiSettings';
import {
  fetchAppliedJobs,
  fetchJobs,
  fetchJobsPublic,
  fetchSavedJobs,
  fetchRecommendedJobs,
  getCounters
} from '../../actions/jobs';
import { useQuery, usePrevious } from '../../utils/hooks';

function Jobs(props) {
  const { isLoggedIn, msalInstance } = props;

  const dispatch = useDispatch();
  const [queries, setQueries] = useSearchParams();
  const filterPeriods = useSelector((state) => state.constFromApi.filterPeriods);
  const jobsData = useSelector((state) => state.jobs.response);
  const recommendedJobs = useSelector((state) => state.jobs.recommendedJobs);
  const counters = useSelector((state) => state.jobs.counters);
  const listCounter = useSelector((state) => state.jobs.pagination?.TotalRecords);
  const [savedJobs, setSavedJobs] = useState([]);
  const completedProfile = localStorage.getItem('userProfileCompleted') === 'true';
  const [searchLengthValidationError, setSearchLengthValidationError] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { t } = useTranslation();

  const query = useQuery();

  const initSearchState = {
    industry: query.getAll('industry') || [],
    function: query.getAll('function') || [],
    contract_type: query.getAll('contract_type') || [],
    date_posted: query.getAll('date_posted') || [],
    IsActive: query.get('IsActive') || false
  };

  const initStringSearchState = {
    field: query.get('field') || '',
    location: query.get('location') || ''
  };

  const initPageNumber = Number(query.get('page')) || 1;
  const [currentPage, setCurrentPage] = useState(initPageNumber);

  const [prevSearchParams, setPrevSearchParams] = useState({ ...initSearchState, ...initStringSearchState });

  const baseTabs = [
    {
      title: t('dashboard.jobs.feed'),
      value: 'feed',
      component: (
        <JobsFeed
          savedJobs={savedJobs}
          jobsData={jobsData}
          msalInstance={msalInstance}
          counter={listCounter}
          currentPage={Number(currentPage)}
          setCurrentPage={setCurrentPage}
        />
      ),
      badgeText: isLoggedIn ? counters?.TotalJobs : ''
    }
  ];

  const authTabs = [
    {
      title: t('dashboard.jobs.applied'),
      value: 'applied',
      component: (
        <AppliedJobs
          jobsData={jobsData}
          msalInstance={msalInstance}
          counter={listCounter}
          recommendedJobs={recommendedJobs}
          currentPage={Number(currentPage)}
          setCurrentPage={setCurrentPage}
        />
      ),
      badgeText: counters?.AppliedJobs
    },
    {
      title: t('profile.tabs.saved'),
      value: 'saved',
      component: (
        <SavedJobs
          jobsData={jobsData}
          msalInstance={msalInstance}
          counter={listCounter}
          recommendedJobs={recommendedJobs}
          searchParams={prevSearchParams}
          currentPage={Number(currentPage)}
          setCurrentPage={setCurrentPage}
        />
      ),
      badgeText: counters?.SavedJobs
    }
    // {
    //     title: 'Inquiries',
    //     value: 'inquiries',
    //     component: null,
    //     badgeText: counters?.Inquiries
    // },
  ];

  const getTabs = (loggedIn) => (loggedIn ? [...baseTabs, ...authTabs] : baseTabs);

  const tabs = getTabs(isLoggedIn);

  const [currentTab, setCurrentTab] = useState(tabs.find((tab) => tab.value === query.get('tab')) || tabs[0]);
  const prevTab = usePrevious(currentTab);
  const [searchParams, setSearchParams] = useState(initSearchState);
  const [stringSearchParams, setStringSearchParams] = useState(initStringSearchState);
  const prevStringSearchParams = usePrevious(stringSearchParams);

  const submitSearch = async (options) => {
    const paramsForSearch = { ...searchParams, ...stringSearchParams };
    const newSearchParamsChanges = {};
    const pageForRequest = { PageNumber: currentPage, PageSize: 10 };

    if (
      Object.entries(prevSearchParams).some((item) => {
        const [key, value] = item;
        if (paramsForSearch[key] !== value && paramsForSearch[key]) {
          newSearchParamsChanges[key] = paramsForSearch[key];
          return true;
        }
        return false;
      })
    ) {
      pageForRequest.PageNumber = 1;
      setCurrentPage(1);
      setQueries({
        ...queries,
        page: 1
      });
      setPrevSearchParams({ ...prevSearchParams, ...newSearchParamsChanges });
    }

    const resultPage = options?.withResetPage ? { PageNumber: 1, PageSize: 10 } : pageForRequest;

    switch (currentTab.value) {
      case 'feed':
        try {
          if (!searchLengthValidationError) {
            if (isAuthenticated) {
              dispatch(fetchJobs(resultPage, paramsForSearch, filterPeriods));
            } else {
              dispatch(fetchJobsPublic(resultPage, paramsForSearch, filterPeriods));
            }
          }
        } catch (err) {
          console.warn(err.message);
        }
        break;
      case 'applied':
        dispatch(fetchAppliedJobs(resultPage, paramsForSearch, filterPeriods));
        break;
      case 'saved':
        // should be changed after saved-jobs starts working
        // dispatch(fetchSavedJobs(page, searchParams));
        try {
          dispatch(fetchSavedJobs(resultPage, paramsForSearch, filterPeriods));
        } catch {
          dispatch(fetchJobs(resultPage, paramsForSearch, filterPeriods));
        }
        break;
      // case 'inquiries':
      //     break;
      default:
        break;
    }
  };

  const searchChangeHandler = (e, prop) => {
    setSearchParams({ ...searchParams, [prop]: e.target.value });
  };

  const searchStringChangeHandler = (e, prop) => {
    setStringSearchParams({ ...stringSearchParams, [prop]: e.target.value });
  };

  const setAlert = async (e, values) => {
    await api
      .post(`v1/job/alert`, { ...values, IsActive: e })
      .then(() => {
        setSearchParams({
          ...searchParams,
          IsActive: e
        });
      })
      .catch(() => {
        console.log('At least 1 field must be selected');
      });
  };

  const changeTab = (tabName) => {
    setCurrentPage(1);
    setCurrentTab(tabName);
  };

  const fetchCounters = async () => {
    dispatch(getCounters());
  };

  const getRecommendedJobs = () => dispatch(fetchRecommendedJobs());

  const resetSearch = () => {
    setSearchParams();
    setStringSearchParams({
      location: '',
      field: ''
    });
  };

  useMemo(() => {
    setCurrentTab(tabs.find((tab) => tab.value === query.get('tab')) || tabs[0]);
  }, [query]);

  const mounted = useRef();
  useEffect(() => {
    if (!mounted.current) {
      setQueries({
        ...queries,
        page: query.get('page') || 1
      });
      setCurrentPage(query.get('page') || 1);
      mounted.current = true;
    } else {
      if (currentTab.value === prevTab?.value) {
        submitSearch();
      }
      if (isAuthenticated && completedProfile) {
        getRecommendedJobs();
      }
    }
  }, [currentPage, isAuthenticated, searchParams]);

  useEffect(() => {
    if (currentTab.value === prevTab?.value) return;
    setQueries({ tab: currentTab.value, page: 1 });
    submitSearch({ withResetPage: true });
    if (isAuthenticated && completedProfile) {
      getRecommendedJobs();
    }
  }, [currentTab]);

  useEffect(() => {
    if (jobsData) {
      setSavedJobs(jobsData.filter((job) => job.IsSaved));
    }
  }, [jobsData]);

  useEffect(() => {
    if (stringSearchParams.field?.length > 0 && stringSearchParams.field?.length < 3) {
      setSearchLengthValidationError(true);
    } else {
      setSearchLengthValidationError(false);
    }

    if (stringSearchParams.field === '' && prevStringSearchParams?.field) submitSearch();
  }, [stringSearchParams.field]);

  useEffect(() => {
    fetchCounters();
  }, []);

  useEffect(() => {
    if (stringSearchParams.location === '' && prevStringSearchParams?.location) submitSearch();
  }, [stringSearchParams.location]);

  return (
    <section className="container-fluid  p-0 m-0  ">
      <Stack px={{ xs: 2, sm: 5 }} className={`container-fluid pt-3 ${classes.tab_container}`}>
        <Tabs tabs={tabs} currentTab={currentTab} setTab={(tabName) => changeTab(tabName)}>
          <div className="container-fluid px-0">
            {query.get('action') === 'apply' && <QuickApply />}
            <JobsFilter
              key={{ ...searchParams, ...stringSearchParams }}
              changeHandler={searchChangeHandler}
              stringChangeHandler={searchStringChangeHandler}
              submitSearch={submitSearch}
              handleReset={resetSearch}
              setAlert={setAlert}
              values={searchParams}
              fieldsValues={stringSearchParams}
              initialSet={setSearchParams}
              initialStringSet={setStringSearchParams}
              searchLengthValidationError={searchLengthValidationError}
            />
            <hr className={classes.divider} />
            {tabs.find((tab) => tab.value === currentTab.value).component}
          </div>
        </Tabs>
      </Stack>
    </section>
  );
}

export default Jobs;
